import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, 
  AlertTriangle, 
  Clock, 
  TrendingUp,
  ChevronRight,
  RefreshCw,
  Filter,
  FileText,
  User,
  Calendar,
  ExternalLink,
  CheckCircle
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import api from '@/lib/api';

const MissedClaimsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [revenueImpact, setRevenueImpact] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [daysBack, setDaysBack] = useState('90');

  const fetchMissedClaims = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('days_back', daysBack);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (typeFilter === 'SHIFT') {
        params.append('include_shifts', 'true');
        params.append('include_timesheets', 'false');
      } else if (typeFilter === 'TIMESHEET') {
        params.append('include_shifts', 'false');
        params.append('include_timesheets', 'true');
      }
      
      const [claimsRes, impactRes] = await Promise.all([
        api.get(`/missed-claims?${params}`),
        api.get('/missed-claims/revenue-impact')
      ]);
      
      setItems(claimsRes.data?.data?.items || []);
      setSummary(claimsRes.data?.data?.summary || null);
      setRevenueImpact(impactRes.data?.data?.revenue_impact_by_period || []);
    } catch (err) {
      notify.error(err, 'MISSED_CLAIMS_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  }, [daysBack, priorityFilter, typeFilter]);

  useEffect(() => {
    fetchMissedClaims();
  }, [fetchMissedClaims]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH': return 'bg-red-500 text-white';
      case 'MEDIUM': return 'bg-orange-500 text-white';
      case 'LOW': return 'bg-blue-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'SHIFT': return <Clock className="w-5 h-5" />;
      case 'TIMESHEET': return <FileText className="w-5 h-5" />;
      default: return <DollarSign className="w-5 h-5" />;
    }
  };

  const handleItemClick = (item) => {
    if (item.type === 'SHIFT') {
      navigate(`/roster/shifts/${item.entity_id}`);
    } else if (item.type === 'TIMESHEET') {
      navigate(`/roster/timesheets`);
    }
  };

  const handleCreateTimesheet = async (item) => {
    if (item.type === 'SHIFT') {
      // Navigate to create timesheet for this shift
      navigate(`/roster/timesheets/create?shift_id=${item.entity_id}`);
    }
  };

  const SummaryCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <Card className={`border-l-4 ${color}`}>
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
        <Icon className="w-8 h-8 text-gray-400" />
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 space-y-6" data-testid="missed-claims-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Missed Claims Detection</h1>
          <p className="text-gray-500">Identify unbilled services and recover lost revenue</p>
        </div>
        <Button 
          onClick={fetchMissedClaims} 
          variant="outline"
          disabled={loading}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard
            title="Total Missed"
            value={summary.total_missed_count || 0}
            subtitle="unbilled items"
            icon={AlertTriangle}
            color="border-red-500"
          />
          <SummaryCard
            title="Estimated Value"
            value={`$${(summary.total_estimated_value || 0).toLocaleString()}`}
            subtitle="potential revenue"
            icon={DollarSign}
            color="border-green-500"
          />
          <SummaryCard
            title="High Priority"
            value={summary.by_priority?.HIGH || 0}
            subtitle=">30 days old"
            icon={Clock}
            color="border-orange-500"
          />
          <SummaryCard
            title="Shifts Missing Timesheets"
            value={summary.by_type?.SHIFT || 0}
            subtitle="need action"
            icon={FileText}
            color="border-blue-500"
          />
        </div>
      )}

      {/* Revenue Impact Chart */}
      {revenueImpact.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Revenue Impact by Period
            </CardTitle>
            <CardDescription>
              Unbilled services accumulating over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              {revenueImpact.map((period) => (
                <div 
                  key={period.period_days} 
                  className="p-4 bg-gray-50 rounded-lg text-center"
                >
                  <p className="text-sm text-gray-500">{period.period_days} Days</p>
                  <p className="text-xl font-bold text-gray-900">
                    ${period.estimated_value.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400">
                    {period.missed_count} items · {period.total_hours}h
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-500">Filters:</span>
        </div>
        <Select value={daysBack} onValueChange={setDaysBack}>
          <SelectTrigger className="w-32" data-testid="days-filter">
            <SelectValue placeholder="Period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30">30 Days</SelectItem>
            <SelectItem value="60">60 Days</SelectItem>
            <SelectItem value="90">90 Days</SelectItem>
            <SelectItem value="180">180 Days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-36" data-testid="priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="HIGH">High Priority</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
            <SelectItem value="LOW">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40" data-testid="type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="SHIFT">Shifts Only</SelectItem>
            <SelectItem value="TIMESHEET">Timesheets Only</SelectItem>
          </SelectContent>
        </Select>
        {(priorityFilter !== 'all' || typeFilter !== 'all' || daysBack !== '90') && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => { 
              setPriorityFilter('all'); 
              setTypeFilter('all'); 
              setDaysBack('90');
            }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Top Participants with Missed Claims */}
      {summary?.by_participant?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Top Participants by Missed Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {summary.by_participant.slice(0, 5).map((p, idx) => (
                <div 
                  key={p.participant_id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                  onClick={() => navigate(`/participants/${p.participant_id}`)}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium">
                      {idx + 1}
                    </span>
                    <span className="font-medium">{p.participant_name || 'Unknown'}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500">{p.count} items</span>
                    <span className="font-bold text-green-600">${p.estimated_value.toLocaleString()}</span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Missed Claims List */}
      <Card>
        <CardHeader>
          <CardTitle>Missed Claim Items</CardTitle>
          <CardDescription>
            {items.length} items found requiring attention
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
              <p className="font-medium">No missed claims found!</p>
              <p className="text-sm">All services have been properly billed.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item, index) => (
                <div
                  key={`${item.type}-${item.entity_id}-${index}`}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`missed-claim-item-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${item.type === 'SHIFT' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                      {getTypeIcon(item.type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.service_name || 'Service'}</span>
                        <Badge className={getPriorityColor(item.priority)}>
                          {item.priority}
                        </Badge>
                        <Badge variant="outline">
                          {item.type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {item.participant_name || 'Unknown'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {item.service_date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {item.hours}h
                        </span>
                        <span className="text-gray-400">
                          {item.days_old} days old
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {item.reason}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">
                        ${item.estimated_value.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-400">est. value</p>
                    </div>
                    <div className="flex gap-2">
                      {item.type === 'SHIFT' && (
                        <Button 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); handleCreateTimesheet(item); }}
                          data-testid={`create-timesheet-${index}`}
                        >
                          Create Timesheet
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleItemClick(item)}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recommendation */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-blue-900">Recommendation</p>
              <p className="text-sm text-blue-700">
                Focus on HIGH priority items first (services older than 30 days) to recover the most value. 
                Create timesheets for completed shifts to include them in your next claim batch.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MissedClaimsPage;
